import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import { MODEL_PROVIDERS, findModel, getDefaultModel } from '../utils/models';

/**
 * Cascading model picker: a trigger, then "Decide for me" plus provider
 * rows (Moonshot / Anthropic / Google / OpenAI) that expand into their
 * models.
 *
 * "Decide for me" emits null — deliberately NOT a model id. Every flow
 * fallback reads `htmlModel || getDefaultModel()`, so an unset value IS
 * the product default, and if the default changes later, decks picked as
 * "decide for me" follow it instead of being pinned to whatever it was
 * that day.
 *
 * The menu renders through a PORTAL with fixed positioning: the r88
 * review found the in-tree absolute menu clipped to a sliver by the
 * Dashboard prompt card's overflow-hidden (the Radix picker it replaced
 * had portaled for the same reason). Portal keeps the
 * data-wiz-dropdown-open marker inside `document`, which the settings
 * modal's Escape guard queries.
 */

const MENU_W = 256;   // w-64
const MENU_H = 340;   // approximate open height, used only for flip-up

export default function ModelSelect({ value, onChange, className = '', variant = 'pill' }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);   // provider id
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // the menu lives in a portal, so "outside" means outside BOTH the
      // trigger and the menu — checking only the trigger would close the
      // menu on the mousedown that precedes every item click
      if (ref.current && !ref.current.contains(e.target)
          && menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = (e) => {
      // scrolling INSIDE the menu is fine; any other scroll detaches a
      // fixed-position menu from its trigger, so close instead of drift
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const selected = value ? findModel(value) : null;
  // Unset ALWAYS renders as the model it resolves to — "Decide for me" as
  // a pill label hid which model would actually run (the user's 10/08
  // complaint: "the model in the prompt doesn't reflect the default").
  const fallback = findModel(getDefaultModel());
  const shown = selected || fallback;

  const toggleOpen = () => {
    if (open) { setOpen(false); return; }
    const r = ref.current.getBoundingClientRect();
    let left = variant === 'toolbar' ? r.left : r.right - MENU_W;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_W - 8));
    const flipUp = r.bottom + 8 + MENU_H > window.innerHeight && r.top > MENU_H + 8;
    setMenuPos(flipUp
      ? { left, bottom: window.innerHeight - r.top + 8 }
      : { left, top: r.bottom + 8 });
    setExpanded(null);
    setOpen(true);
  };

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setExpanded(null);
  };

  const triggerClass = variant === 'toolbar'
    ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
    : 'inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-foreground hover:bg-muted/60 transition-colors';

  const menu = (
    <div
      ref={menuRef}
      data-wiz-dropdown-open="1"
      style={{ position: 'fixed', left: menuPos?.left, top: menuPos?.top, bottom: menuPos?.bottom }}
      className="z-[200] w-64 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-card shadow-lg"
    >
      <button
        type="button"
        onClick={() => pick(null)}
        className="flex w-full items-center justify-between px-3.5 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors"
      >
        <span className="min-w-0 text-left">
          <span className="block font-medium">Decide for me</span>
          {fallback && (
            <span className="block text-[11px] text-muted-foreground">Currently {fallback.label}</span>
          )}
        </span>
        {!value && <Check className="w-4 h-4 shrink-0" />}
      </button>
      <div className="border-t border-border" />
      {MODEL_PROVIDERS.map((p) => (
        <div key={p.id}>
          <button
            type="button"
            onClick={() => setExpanded(expanded === p.id ? null : p.id)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors"
          >
            <span className="flex items-center gap-2.5">
              <span className={`inline-flex min-w-[20px] h-5 items-center justify-center rounded-full text-[11px] font-bold ${p.badgeClass}`}>
                {p.badge}
              </span>
              {p.label}
            </span>
            <span className="flex items-center gap-1.5">
              {/* a dot marks the provider of the current selection */}
              {selected?.provider.id === p.id && (
                <span className="w-1.5 h-1.5 rounded-full bg-foreground/60" />
              )}
              <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expanded === p.id ? 'rotate-90' : ''}`} />
            </span>
          </button>
          {expanded === p.id && (
            <div className="bg-muted/30">
              {p.models.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => pick(m.value)}
                  className="flex w-full items-start justify-between gap-2 pl-11 pr-3.5 py-2 text-left hover:bg-muted/60 transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground">{m.label}</span>
                    {m.desc && (
                      <span className="block text-[11px] text-muted-foreground leading-snug">{m.desc}</span>
                    )}
                  </span>
                  {value === m.value && <Check className="w-4 h-4 shrink-0 mt-0.5" />}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={toggleOpen}
        className={triggerClass}
        title="AI model"
      >
        {shown && (
          <>
            <span className={`inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full text-[10px] font-bold ${shown.provider.badgeClass}`}>
              {shown.provider.badge}
            </span>
            <span className="font-medium">{shown.label}</span>
            {!selected && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">auto</span>
            )}
          </>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menuPos && createPortal(menu, document.body)}
    </div>
  );
}
